import { QUERY_KEYS } from "@repo/baseCloud";
import { holdPropsInUtc } from "@repo/helpers";
import { FilterItem, keyPair, MvcBranch, MvcLocation } from "@repo/models";
import { useQueryClient } from "@tanstack/react-query";

import { useLocationContext } from "../contexts/editableItemContext";
import { LocationStatus } from "../models";
import { useLocationPortalApi, useQueryWrapper } from "./locationPortalApi";
import * as Queries from "./queries";

/**
 * The functions are only called (in handlers and query functions), never compared, so they aren't memoized.
 * The returned object is new on every render anyway.
 */
export function useLocationApi() {
	const { Get, Post } = useLocationPortalApi();
	const queryClient = useQueryClient();

	async function saveLocation(location: MvcLocation) {
		const toSave = {
			...location,
			detailsDict: Object.fromEntries(
				Object.entries(location.details ?? {}).map(([key, value]) => {
					return [`${key.charAt(0).toUpperCase()}${key.slice(1)}`, value];
				}),
			),
		};

		const locationId = await Post<number, MvcLocation>("SaveLocation", null, toSave);
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.Locations.Locations] }),
			queryClient.invalidateQueries({ queryKey: [...Queries.LOCATION.DETAILS, locationId], exact: true }),
		]);
		return locationId;
	}

	function updateLocationStatus(locationId: number, status: Partial<LocationStatus>) {
		return Post<string>("UpdateLocationStatus", null, { locationId, status });
	}

	function loadLocation(id: number) {
		return Get<MvcLocation>("GetLocation", { id }).then(initializeLocation);
	}

	async function getLocations() {
		return await Get<keyPair[]>("GetLocations");
	}

	async function getAllLocations() {
		const locations = await Get<MvcLocation[]>("GetAllLocations");
		return locations.map(initializeLocation);
	}

	async function getStatusHistories(locationId: number) {
		const histories = await Get<LocationStatus[]>("GetStatusHistories", { locationId });
		for (let history of histories) holdPropsInUtc(history, "startDate", "endDate");
		return histories;
	}

	const getBrands = () => Get<FilterItem[]>("GetBrands");
	const getChannels = () => Get<FilterItem[]>("GetChannels");
	const getCompanyDivisions = () => Get<FilterItem[]>("GetCompanyDivisions");
	const getReps = () => Get<FilterItem[]>("GetReps");
	const getCountries = () => Get<FilterItem[]>("GetCountries");
	const getStatesByCountry = () => Get<StateWithCountry[]>("GetStates");
	const getRetailPartnerTypes = () => Get<FilterItem[]>("GetRetailPartnerTypes");
	const getRetailPartners = () => Get<RetailPartner[]>("GetRetailPartners");
	const getLocationStatuses = () => Get<FilterItem[]>("GetLocationStatuses");

	const getAllBranches = () => Get<MvcBranch[]>("GetAllBranches");

	return {
		saveLocation,
		loadLocation,
		getLocations,
		getAllLocations,
		getBrands,
		getChannels,
		getCompanyDivisions,
		getReps,
		getCountries,
		getStatesByCountry,
		getRetailPartnerTypes,
		getRetailPartners,
		getStatusHistories,
		updateLocationStatus,
		getLocationStatuses,
		getAllBranches,
	};
}

export function useLocationQueries() {
	const { loadLocation, getStatusHistories } = useLocationApi();
	const { locationId } = useLocationContext();

	const useLocationInfoQuery = (setLocationId?: number) =>
		useQueryWrapper(
			Queries.LOCATION.DETAILS,
			[setLocationId ?? locationId],
			() => loadLocation(setLocationId ?? locationId),
			{
				staleTime: Infinity,
				enabled: !!setLocationId || !!locationId,
			},
		);

	const useStatusHistoriesQuery = (locationId: number) =>
		useQueryWrapper(Queries.LOCATION.STATUS_HISTORIES, [locationId], () => getStatusHistories(locationId), {
			enabled: !!locationId,
		});

	return {
		useLocationInfoQuery,
		useStatusHistoriesQuery,
	};
}

function initializeLocation(location: MvcLocation) {
	location.stateId = location.stateId || null;
	return location;
}

export interface RetailPartner extends FilterItem {
	typeId: number;
	code: string;
}
interface StateWithCountry {
	usStateId: number;
	name: string;
	countryId: number;
}
